<template>
  <transition name="cart-item-edit">
    <div
      v-if="visible"
      class="cart-item-edit-modal"
      role="dialog"
      aria-modal="true"
      @click.self="cancel"
    >
      <div class="cart-item-edit-dialog">
      <header class="edit-modal-header">
        <div>
          <h2>{{ productName }}</h2>
          <div class="edit-modal-meta">
            <span class="sale-code-meta">
              {{ $t('retailSale.editItem.saleCode') }}:
              {{ selectedSaleCode.sale_code || '-' }}
            </span>
            <span class="meta-separator">•</span>
            <span class="base-price-meta">
              {{ $t('retailSale.editItem.base') }}:
              {{ formattedUnitPrice }}
            </span>
            <span class="meta-separator">•</span>
            <span class="stock-meta">
              <i class="fas fa-circle"></i>
              {{ stockLabel }}
            </span>
          </div>
        </div>
        <button
          type="button"
          class="edit-modal-close"
          :aria-label="$t('retailSale.editItem.close')"
          @click="cancel"
        >
          <i class="fas fa-times"></i>
        </button>
      </header>

      <div class="edit-modal-body">
        <section class="edit-modal-section">
          <div class="edit-section-heading">
            <strong><i class="fas fa-balance-scale"></i>{{ $t('retailSale.editItem.uomTitle') }}</strong>
            <span>
              {{ $t('retailSale.editItem.default') }}:
              <b>{{ defaultUomName }}</b>
            </span>
          </div>
          <div v-if="loading" class="edit-modal-loading">
            <i class="fas fa-spinner fa-spin"></i>
            {{ $t('retailSale.editItem.loadingUom') }}
          </div>
          <div v-else class="uom-options">
            <button
              v-for="saleCode in saleCodes"
              :key="saleCode.pro_sale_code_id"
              type="button"
              class="uom-option"
              :class="{ active: saleCode.pro_sale_code_id === draft.pro_sale_code_id }"
              @click="selectSaleCode(saleCode)"
            >
              <span class="uom-option-name">
                {{ saleCodeName(saleCode) }}
                <i v-if="saleCode.is_default" class="fas fa-check-circle"></i>
              </span>
              <strong>{{ formatCurrency(saleCode.price) }}</strong>
              <small v-if="saleCode.promotion" class="uom-promotion">
                {{ promotionLabel(saleCode.promotion) }}
              </small>
              <small v-else>{{ $t('retailSale.editItem.standard') }}</small>
            </button>
          </div>
        </section>

        <section class="edit-modal-section">
          <div class="edit-section-heading">
            <strong><i class="fas fa-shopping-basket"></i>{{ $t('retailSale.editItem.quantityTitle') }}</strong>
            <span>
              {{ $t('retailSale.editItem.unitWeight') }}:
              <b>{{ selectedSaleCode.uom_name_en || '-' }}</b>
            </span>
          </div>
          <div class="quantity-editor">
            <div class="quantity-control">
              <button type="button" @click="changeQuantity(-1)">−</button>
              <input
                v-model.number="draft.quantity"
                class="quantity-input"
                type="number"
                min="0.001"
                step="any"
                :aria-label="$t('retailSale.editItem.quantityInput')"
                @focus="$event.target.select()"
                @mouseup.prevent
                @blur="normalizeQuantity"
              />
              <button type="button" @click="changeQuantity(1)">+</button>
            </div>
            <span>{{ $t('retailSale.editItem.presets') }}:</span>
            <button
              v-for="preset in quantityPresets"
              :key="preset"
              type="button"
              class="quantity-preset"
              :class="{ active: draft.quantity === preset }"
              @click="setQuantity(preset)"
            >
              {{ preset }}
            </button>
          </div>
        </section>

        <section class="edit-modal-section">
          <div class="edit-section-heading">
            <strong><i class="fas fa-tag"></i>{{ $t('retailSale.editItem.discountTitle') }}</strong>
            <div class="discount-type-switch">
              <button
                type="button"
                :class="{ active: draft.discount_type === '$' }"
                @click="setDiscountType('$')"
              >
                {{ $t('retailSale.editItem.amount') }}
              </button>
              <button
                type="button"
                :class="{ active: draft.discount_type === '%' }"
                @click="setDiscountType('%')"
              >
                {{ $t('retailSale.editItem.percent') }}
              </button>
            </div>
          </div>
          <div class="discount-editor">
            <div class="discount-input-wrap">
              <span>{{ draft.discount_type }}</span>
              <input v-model.number="draft.discount_rate" type="number" min="0" step="0.01" @focus="$event.target.select()" @mouseup.prevent />
            </div>
            <button
              v-for="value in discountPresets"
              :key="value"
              type="button"
              class="discount-preset"
              :class="{ active: Number(draft.discount_rate) === value }"
              @click="setDiscountRate(value)"
            >
              {{ draft.discount_type === '%' ? `${value}%` : formatCurrency(value) }}
            </button>
          </div>
          <div class="discount-summary">
            <span>
              {{ $t('retailSale.editItem.subtotal') }}
              <s>{{ formatCurrency(grossAmount) }}</s>
            </span>
            <strong>
              <i class="fas fa-arrow-right"></i>
              {{ $t('retailSale.editItem.discounted') }}
              {{ formatCurrency(amounts.total_after_discount) }}
            </strong>
            <span class="discount-saved">
              {{ $t('retailSale.editItem.save') }}
              {{ formatCurrency(amounts.discount_amount) }}
              · {{ discountPercent }}% {{ $t('retailSale.editItem.off') }}
            </span>
          </div>
        </section>

        <section class="edit-modal-section instruction-section">
          <div class="edit-section-heading">
            <strong><i class="fas fa-sticky-note"></i>{{ $t('retailSale.editItem.noteTitle') }}</strong>
          </div>
          <textarea v-model="draft.remarks" rows="2" :placeholder="$t('retailSale.editItem.notePlaceholder')"></textarea>
        </section>
      </div>

      <footer class="edit-modal-footer">
        <button type="button" class="remove-item-button" @click="remove">
          <i class="fas fa-trash-alt"></i>
          {{ $t('retailSale.editItem.remove') }}
        </button>
        <div class="edit-modal-actions">
          <button type="button" class="cancel-button" @click="cancel">
            <i class="fas fa-times"></i>
            {{ $t('retailSale.editItem.cancel') }}
            <kbd>ESC</kbd>
          </button>
          <button type="button" class="save-button" @click="save">
            <i class="fas fa-check"></i>
            {{ $t('retailSale.editItem.saveButton') }}
            <kbd>ENTER</kbd>
          </button>
        </div>
      </footer>
      </div>
    </div>
  </transition>
</template>

<script>
import { calculateCartItemAmounts } from '../../utils/cart'
import { formatCurrency } from '../../utils/currency'

export default {
  name: 'CartItemEditModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    item: {
      type: Object,
      default: null
    },
    saleCodes: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    currency: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      draft: {},
      quantityPresets: [1, 2, 3, 5, 10, 12],
      discountPresets: [1, 2, 5, 10]
    }
  },
  computed: {
    productName() {
      if (!this.item) return ''
      if (this.item.nameKey) return this.$t(this.item.nameKey)

      const isKhmer = this.$root.$i18n && this.$root.$i18n.locale === 'kh'
      return isKhmer
        ? this.item.nameKh || this.item.name
        : this.item.name || this.item.nameKh
    },
    selectedSaleCode() {
      return (
        this.saleCodes.find(
          saleCode => saleCode.pro_sale_code_id === this.draft.pro_sale_code_id
        ) || this.draft
      )
    },
    defaultUomName() {
      const defaultSaleCode = this.saleCodes.find(saleCode => saleCode.is_default)

      return this.saleCodeName(defaultSaleCode || this.selectedSaleCode)
    },
    formattedUnitPrice() {
      return this.formatCurrency(this.draft.unit_price || 0)
    },
    stockLabel() {
      return this.$t('retailSale.editItem.inStock', {
        stock: this.selectedSaleCode.stock || 0
      })
    },
    grossAmount() {
      return Number(this.draft.quantity || 0) * Number(this.draft.unit_price || 0)
    },
    amounts() {
      return calculateCartItemAmounts(this.draft)
    },
    discountPercent() {
      const grossAmount = Number(this.grossAmount)
      const discountAmount = Number(this.amounts.discount_amount || 0)

      if (!Number.isFinite(grossAmount) || grossAmount <= 0 || !Number.isFinite(discountAmount)) {
        return 0
      }

      return Math.round((discountAmount / grossAmount) * 1000) / 10
    }
  },
  watch: {
    visible(value) {
      if (value) this.resetDraft()
    },
    item: {
      deep: true,
      handler() {
        if (this.visible) this.resetDraft()
      }
    },
    saleCodes() {
      if (this.visible && this.saleCodes.length && !this.draft.pro_sale_code_id) {
        this.selectSaleCode(this.saleCodes[0])
      }
    }
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeydown)
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeydown)
  },
  methods: {
    formatCurrency(value) {
      return formatCurrency(value, this.currency)
    },
    saleCodeName(saleCode) {
      if (!saleCode) return '-'

      return this.$root.$i18n && this.$root.$i18n.locale === 'kh'
        ? saleCode.uom_name_kh || saleCode.uom_name_en
        : saleCode.uom_name_en || saleCode.uom_name_kh
    },
    promotionLabel(promotion) {
      return this.$t('retailSale.product.discountBadge', {
        discount: promotion.discount_rate
      })
    },
    resetDraft() {
      if (!this.item) return

      this.draft = {
        ...this.item,
        quantity: Number(this.item.quantity || 1),
        discount_rate: Number(this.item.discount_rate || 0),
        remarks: this.item.remarks || ''
      }
    },
    selectSaleCode(saleCode) {
      if (!saleCode) return

      this.draft = {
        ...this.draft,
        pro_sale_code_id: saleCode.pro_sale_code_id,
        uom_id: saleCode.uom_id,
        uom_name_en: saleCode.uom_name_en,
        uom_name_kh: saleCode.uom_name_kh,
        unit_price: Number(saleCode.price || 0),
        promotion: saleCode.promotion || null,
        promo_type_id: saleCode.promotion ? saleCode.promotion.promo_type_id : null,
        discount_type: saleCode.promotion ? '%' : this.draft.discount_type || '%',
        discount_rate: saleCode.promotion
          ? Number(saleCode.promotion.discount_rate || 0)
          : 0
      }
    },
    changeQuantity(amount) {
      this.setQuantity(this.normalizedQuantity() + amount)
    },
    setQuantity(quantity) {
      const value = Number(quantity)
      this.draft.quantity = Number.isFinite(value) && value >= 1 ? value : 1
    },
    normalizedQuantity() {
      const value = Number(this.draft.quantity)

      return Number.isFinite(value) && value > 0 ? value : 1
    },
    normalizeQuantity() {
      this.setQuantity(this.draft.quantity)
    },
    setDiscountType(type) {
      this.draft.discount_type = type
      this.draft.discount_rate = 0
    },
    setDiscountRate(rate) {
      this.draft.discount_rate = Number(rate || 0)
    },
    handleKeydown(event) {
      if (!this.visible) return
      if (event.key === 'Escape') this.cancel()
      if (event.key === 'Enter' && event.target.tagName !== 'TEXTAREA') this.save()
    },
    cancel() {
      this.$emit('cancel')
    },
    remove() {
      this.$emit('remove', this.item)
    },
    save() {
      this.normalizeQuantity()
      const updatedItem = {
        ...this.draft,
        ...this.amounts
      }

      this.$emit('save', updatedItem)
    }
  }
}
</script>

<style scoped src="../../styles/cart-item-edit-modal.css"></style>
